<template>
  <div class="nav-bar-wrapper">
    <v-app-bar elevation="2" color="white" height="60" fixed class="nav-bar">
      <!-- Logo Section -->
      <div class="logo-container">
        <v-img src="/logo-placeholder.svg?height=40&width=120&query=company logo" max-height="40" contain
          class="logo-image"></v-img>
      </div>

      <v-spacer></v-spacer>

      <!-- Desktop Navigation Menu -->
      <div class="desktop-menu hidden-sm-and-down">
        <v-btn v-for="item in menu" :key="item.id" v-if="item.show" :to="item.link" text class="nav-btn"
          color="#0d2c6d">
          <v-icon left>{{ item.icon }}</v-icon>
          <span>{{ item.title }}</span>
        </v-btn>

        <!-- External link if exists -->
        <v-btn v-if="specialPage" :href="dynamicLink(specialPage)" text class="nav-btn" color="#0d2c6d" target="_blank">
          <v-icon left>mdi-dots-horizontal</v-icon>
          <span>אפשרויות נוספות</span>
        </v-btn>
      </div>

      <v-spacer></v-spacer>

      <!-- User Profile Section -->
      <div class="user-section">
        <!-- Role Switch Button -->
        <v-tooltip bottom v-if="isAgent || isAdmin">
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on" @click="isAgent ? swichBackToAgent() : swichBackToAdmin()" class="role-switch-btn">
              <v-icon color="#0d2c6d">mdi-swap-horizontal</v-icon>
            </v-btn>
          </template>
          <span>חזור לניהול</span>
        </v-tooltip>

        <!-- User Profile Menu -->
        <v-menu offset-y transition="slide-y-transition" bottom left min-width="200">
          <template v-slot:activator="{ on, attrs }">
            <div class="user-profile" v-bind="attrs" v-on="on">
              <v-avatar color="#0d2c6d" size="36" class="user-avatar">
                <span class="white--text text-subtitle-2">{{ userInitials }}</span>
              </v-avatar>
              <div class="user-info d-none d-sm-block">
                <div class="user-name">{{ userName }}</div>
                <div class="user-email">{{ email }}</div>
              </div>
              <v-icon color="#0d2c6d" size="20" class="dropdown-icon">mdi-chevron-down</v-icon>
            </div>
          </template>

          <v-card class="user-menu-card">
            <v-list>
              <v-list-item class="user-menu-header">
                <v-list-item-avatar>
                  <v-avatar color="#0d2c6d" size="40">
                    <span class="white--text">{{ userInitials }}</span>
                  </v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="user-menu-name">{{ userName }}</v-list-item-title>
                  <v-list-item-subtitle class="user-menu-email">{{ email }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item @click="showLogs" v-if="canViewLogs" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#0d2c6d">mdi-history</v-icon>
                </v-list-item-icon>
                <v-list-item-title>צפה בלוגים</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="hasMultipleWorkspaces" @click="switchWorkspace" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#0d2c6d">mdi-swap-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title>החלף סביבת עבודה</v-list-item-title>
              </v-list-item>

              <v-list-item @click="logOut" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#f44336">mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="logout-text">התנתק</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>

      <!-- Mobile Menu -->
      <div class="mobile-menu hidden-md-and-up">
        <v-menu bottom left transition="slide-y-transition" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" class="menu-btn">
              <v-icon color="#0d2c6d">mdi-menu</v-icon>
            </v-btn>
          </template>

          <v-card class="mobile-menu-card">
            <v-list>
              <v-list-item v-for="item in menu" :key="item.id" v-if="item.show" v-bind="getItemAttributes(item)"
                class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#0d2c6d">{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>

              <v-divider></v-divider>

              <v-list-item :href="dynamicLink(specialPage)" v-if="specialPage" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#0d2c6d">mdi-dots-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title>אפשרויות נוספות</v-list-item-title>
              </v-list-item>

              <v-list-item @click="showLogs" v-if="canViewLogs" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#0d2c6d">mdi-history</v-icon>
                </v-list-item-icon>
                <v-list-item-title>צפה בלוגים</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="hasMultipleWorkspaces" @click="switchWorkspace" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#f44336">mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="logout-text">החלף סביבת עבודה</v-list-item-title>
              </v-list-item>

              <v-list-item @click="logOut" class="menu-item">
                <v-list-item-icon>
                  <v-icon color="#f44336">mdi-logout</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="logout-text">התנתק</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </v-app-bar>

    <!-- Loading Overlay -->
    <v-overlay :value="overlay" class="loading-overlay">
      <v-progress-circular indeterminate size="64" width="5" color="#0d2c6d"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Auth from '@/services/auth.service.js'

export default {
  data() {
    return {
      menu: [],
      userMenu: [
        { id: "database", title: "נתונים", link: "/user", show: true, icon: "mdi-database-outline" },
        { id: "guides", title: "מדריכים", link: "/user_guides", show: true, icon: "mdi-book-open-variant-outline" },
        { id: "contact_us", title: "צור קשר", link: "/user_contact_us", show: true, icon: "mdi-email-outline" },
        { id: "user_account", title: "אזור אישי", link: "/user_account", show: true, icon: "mdi-account-outline" },
        { id: "user_workspaces", title: "", link: "/user_workspaces", show: false, icon: "mdi-account-group" },
      ],
      adminMenu: [
        { id: "admin_users_list", title: "ניהול משתמשים", link: "/manage_users", icon: "mdi-account-outline", show: true },
        { id: "admin_permissions", title: "ניהול הרשאות", link: "/manage_permissions", icon: "mdi-key-outline", show: true },
      ],
      userName: "",
      email: "",
      role: "",
      isFocused: false,
      isAdmin: false,
      isAgent: false,
      specialPage: null,
      overlay: false,
      hasMultipleWorkspaces: false
    }
  },
  methods: {
    logOut() {
      localStorage.removeItem("token");
      localStorage.removeItem('hasMultipleWorkspaces');
      this.$router.replace({ name: "login" });
    },

    getItemAttributes(item) {
      if (item.id === 'user_special_page') {
        // When the item's id is 'user_special_page', return an object with the 'href' attribute
        return { href: this.dynamicLink() };
      } else {
        // Otherwise, return an object with the 'to' attribute
        return { to: item.link };
      }
    },

    setNavListIItems() {
      this.userName = Auth.getUserName();
      this.email = Auth.getUserEmail();
      this.role = Auth.getUserRole();

      if (this.role === "admin" || this.role === "superAdmin" || this.role === "agent") {
        this.menu = this.adminMenu;
      } else {
        this.menu = this.userMenu;
      }

      if (localStorage.getItem("tokenAdmin")) {
        this.isAdmin = true;
      }

      if (localStorage.getItem("tokenAgent")) {
        this.isAgent = true;
      }

      this.specialPage = Auth.getUserSpecialPage();
    },

    swichBackToAdmin() {
      this.overlay = true;
      localStorage.removeItem('hasMultipleWorkspaces');
      const currentRole = Auth.getUserRole();
      localStorage.setItem("token", localStorage.getItem("tokenAdmin"));
      localStorage.removeItem("tokenAdmin");
      this.isAdmin = false;

      if (localStorage.getItem("tokenAgent")) {
        localStorage.removeItem("tokenAgent");
      }

      if (currentRole === "user") {
        this.$router.replace({ name: "manageUsers" });
      } else {
        window.location.reload();
      }

      this.overlay = false;
    },

    swichBackToAgent() {
      this.overlay = true;
      localStorage.removeItem('hasMultipleWorkspaces');
      localStorage.setItem("token", localStorage.getItem("tokenAgent"));
      localStorage.removeItem("tokenAgent");
      this.isAgent = false;
      this.$router.replace({ name: "manageUsers" });
      this.overlay = false;
    },
    switchWorkspace() {
      this.$router.replace({ name: "Workspaces" });
    },
    dynamicLink() {
      // Generate the dynamic URL with the parameter
      let token = localStorage.getItem("token");
      return this.specialPage + "?token=" + token;
    },

    showLogs() {
      const isAdminSwitch = localStorage.getItem("tokenAdmin");
      if (this.role === "admin" || this.role === "superAdmin" || (this.role === "user" && isAdminSwitch)) {
        this.$router.push({ name: 'logs' });
      }
    }
  },
  // watch: {
  //   asSpecialPage(newVal) {
  //     // Find the user_special_page item in the userMenu array
  //     const specialPageItem = this.userMenu.find((item) => item.id === "user_special_page");

  //     // Conditionally update the show property based on newVal (asSpecialPage)
  //     if (specialPageItem) {
  //       specialPageItem.show = newVal;
  //     }
  //   },
  // },
  computed: {
    // filteredMenu() {
    //   // If the menu array length is greater than 5, filter it to show only the first 5 items
    //   if (this.menu.length > 4) {
    //     return this.menu.slice(0, 4);
    //   } else {
    //     return this.menu;
    //   }
    // },

    canViewLogs() {
      const isAdminSwitch = localStorage.getItem("tokenAdmin");
      return this.role === "admin" || this.role === "superAdmin" || isAdminSwitch;
    },

    userInitials() {
      return this.userName
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
    },
  },
  mounted() {
    this.setNavListIItems();
    this.hasMultipleWorkspaces = localStorage.getItem('hasMultipleWorkspaces');

  },
}
</script>

<style scoped>
.nav-bar-wrapper {
  width: 100%;
  height: 10px;
}

.nav-bar {
  background: linear-gradient(135deg, #ffb91a 0%, #ffd166 100%) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.logo-container {
  padding: 0 16px;
}

.logo-image {
  transition: all 0.3s ease;
}

.desktop-menu {
  display: flex;
  align-items: center;
}

.nav-btn {
  margin: 0 4px;
  height: 64px;
  border-radius: 0;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
  text-transform: none;
  transition: all 0.3s ease;
  font-family: 'Heebo', sans-serif;
  position: relative;
}

.nav-btn::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background-color: #0d2c6d;
  transition: width 0.3s ease;
}

.nav-btn:hover::after {
  width: 100%;
}

.user-section {
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.role-switch-btn {
  margin-right: 16px;
  background-color: rgba(13, 44, 109, 0.05);
  transition: all 0.3s ease;
}

.role-switch-btn:hover {
  background-color: rgba(13, 44, 109, 0.1);
  transform: translateY(-2px);
}

.user-profile {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 24px;
  transition: all 0.3s ease;
}

.user-profile:hover {
  background-color: rgba(13, 44, 109, 0.05);
}

.user-avatar {
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.8);
}

.user-info {
  margin: 0 8px;
  direction: ltr;
}

.user-name {
  font-size: 14px;
  font-weight: 600;
  color: #0d2c6d;
  line-height: 1.2;
}

.user-email {
  font-size: 12px;
  color: rgba(13, 44, 109, 0.7);
  line-height: 1.2;
}

.dropdown-icon {
  transition: transform 0.3s ease;
}

.user-profile:hover .dropdown-icon {
  transform: rotate(180deg);
}

.user-menu-card {
  border-radius: 8px;
  overflow: hidden;
}

.user-menu-header {
  background-color: rgba(13, 44, 109, 0.05);
  padding: 16px;
}

.user-menu-name {
  font-size: 16px;
  font-weight: 600;
  color: #0d2c6d;
}

.user-menu-email {
  font-size: 12px;
  color: rgba(13, 44, 109, 0.7);
}

.menu-item {
  transition: background-color 0.2s ease;
}

.menu-item:hover {
  background-color: rgba(13, 44, 109, 0.05);
}

.logout-text {
  color: #f44336;
}

.mobile-menu-card {
  border-radius: 8px;
  overflow: hidden;
  width: 250px;
}

.menu-btn {
  margin-left: 8px;
}

.loading-overlay {
  z-index: 1000;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .nav-bar {
    padding: 0 8px;
  }

  .logo-container {
    padding: 0 8px;
  }
}

@media (max-width: 600px) {
  .nav-bar {
    height: 56px;
  }

  .nav-bar-wrapper {
    height: 56px;
  }
}
</style>